import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Piecewise admin dashboard hooks. Each one drives a single section so the
// page can paint as soon as its slice resolves instead of gating everything
// on a single 27-query waterfall. The combined endpoint is still available
// via apiClient.getSystemAnalytics() — those callers (e.g. the Analytics
// page) are unaffected.

type DateRange = { startDate?: Date; endDate?: Date } | undefined;
type Intervals = { userGrowthInterval?: string; revenueInterval?: string } | undefined;

// Date ranges are Date objects so we can't use them as query keys directly —
// serialize to ISO strings (or "undefined") for stable, comparable keys.
const dateRangeKey = (dateRange: DateRange) => ({
  start: dateRange?.startDate?.toISOString() ?? null,
  end: dateRange?.endDate?.toISOString() ?? null,
});

export function useAdminHeader(dateRange: DateRange) {
  return useQuery({
    queryKey: ['admin-analytics', 'header', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getAdminAnalyticsHeader(dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load header analytics');
      }
      return response.data;
    },
  });
}

export function useAdminTrends(dateRange: DateRange, intervals: Intervals) {
  return useQuery({
    queryKey: [
      'admin-analytics',
      'trends',
      dateRangeKey(dateRange),
      intervals?.userGrowthInterval ?? 'monthly',
      intervals?.revenueInterval ?? 'monthly',
    ],
    queryFn: async () => {
      const response = await apiClient.getAdminAnalyticsTrends(dateRange, intervals);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load trends analytics');
      }
      return response.data;
    },
  });
}

export function useAdminRankings(dateRange: DateRange) {
  return useQuery({
    queryKey: ['admin-analytics', 'rankings', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getAdminAnalyticsRankings(dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load rankings analytics');
      }
      return response.data;
    },
    // Rankings is the slow slice ($lookup chains across Order/BulkOrder/
    // Invoice). Cache it longer than the default and don't refetch on
    // remount so navigating back to the dashboard is instant.
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}

export function useSalesRepLeaderboard(dateRange: DateRange) {
  return useQuery({
    queryKey: ['admin-analytics', 'sales-reps', dateRangeKey(dateRange)],
    queryFn: async () => {
      const response = await apiClient.getSalesRepLeaderboard(dateRange);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load sales rep leaderboard');
      }
      return response.data;
    },
    // Aggregates across the full invoice set; cache like rankings.
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}

export function useProductVelocity(dateRange: DateRange, distributorId?: string) {
  return useQuery({
    queryKey: ['admin-analytics', 'product-velocity', dateRangeKey(dateRange), distributorId ?? 'system'],
    queryFn: async () => {
      const response = await apiClient.getProductVelocity(dateRange, distributorId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load product velocity');
      }
      return response.data;
    },
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}

// Account Health tab. Each section loads independently; all four aggregate
// across the full paid-invoice history, so cache them like rankings.
const unwrap = async (promise: Promise<{ success: boolean; data?: any; error?: string }>, fallback: string) => {
  const response = await promise;
  if (!response.success || !response.data) {
    throw new Error(response.error || fallback);
  }
  return response.data;
};

export function useAccountHealthAccounts(params: {
  mode: 'rolling' | 'calendar';
  month?: string;
  threshold: number;
}) {
  return useQuery({
    queryKey: ['admin-analytics', 'account-health', 'accounts', params.mode, params.month ?? null, params.threshold],
    queryFn: () => unwrap(apiClient.getAccountHealthAccounts(params), 'Failed to load account slippage'),
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}

export function useAccountHealthRetention(months: number) {
  return useQuery({
    queryKey: ['admin-analytics', 'account-health', 'retention', months],
    queryFn: () => unwrap(apiClient.getAccountHealthRetention(months), 'Failed to load revenue retention'),
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}

export function useAccountHealthActivation(months: number) {
  return useQuery({
    queryKey: ['admin-analytics', 'account-health', 'activation', months],
    queryFn: () => unwrap(apiClient.getAccountHealthActivation(months), 'Failed to load signup activation'),
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}

export function useAccountHealthRewards(weeks: number) {
  return useQuery({
    queryKey: ['admin-analytics', 'account-health', 'rewards', weeks],
    queryFn: () => unwrap(apiClient.getAccountHealthRewards(weeks), 'Failed to load rewards engagement'),
    staleTime: 5 * 60_000,
    refetchOnMount: false,
  });
}
